import { Button } from "@/components/ui/button";
import { Trash2, Send, Pause, Play, X, Loader2, RotateCcw } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface RecordingBarProps {
  duration: number;
  liveTranscript: string;
  isPaused: boolean;
  onCancel: () => void;
  onSend: () => void;
  onTogglePause: () => void;
  isTranscribing?: boolean;
  transcriptionProgress?: number;
  transcriptionFailed?: boolean;
  onRetry?: () => void;
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const RecordingBar = ({
  duration,
  liveTranscript,
  isPaused,
  onCancel,
  onSend,
  onTogglePause,
  isTranscribing = false,
  transcriptionProgress = 0,
  transcriptionFailed = false,
  onRetry,
}: RecordingBarProps) => {
  // Failed state UI with retry button
  if (transcriptionFailed) {
    return (
      <div className="space-y-2 w-full">
        <div 
          className="flex items-center gap-3 h-12 w-full px-3 bg-card border border-destructive/30 rounded-lg shadow-sm"
          role="region"
          aria-label="Transcription failed"
        >
          {/* Cancel button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </Button>

          {/* Error message */}
          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm text-destructive font-medium">Transcription failed</span>
          </div>

          {/* Retry button */}
          <Button
            onClick={onRetry}
            size="sm"
            variant="outline"
            className="shrink-0 gap-1.5 border-primary text-primary hover:bg-primary/10"
            aria-label="Try again"
          >
            <RotateCcw className="w-4 h-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Transcribing state UI
  if (isTranscribing) {
    return (
      <div className="space-y-2 w-full">
        <div 
          className="flex items-center gap-3 h-12 w-full px-3 bg-card border rounded-lg shadow-sm"
          role="region"
          aria-label="Transcribing audio"
        >
          {/* Cancel button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Cancel transcription"
          >
            <X className="w-5 h-5" />
          </Button>

          {/* Transcribing indicator */}
          <div className="flex items-center gap-2 shrink-0">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Transcribing...</span>
          </div>

          {/* Progress bar */}
          <div className="flex-1">
            <Progress value={transcriptionProgress} className="h-2" />
          </div>

          {/* Progress percentage */}
          <span className="text-xs font-mono text-muted-foreground min-w-[40px] text-right">
            {Math.round(transcriptionProgress)}%
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 w-full">
      {/* Main recording bar */}
      <div 
        className="flex items-center gap-2 h-12 w-full px-3 bg-card border rounded-lg shadow-sm"
        role="region"
        aria-label="Recording in progress"
      >
        {/* Cancel/Trash button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="h-10 w-10 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          aria-label="Cancel recording"
        >
          <Trash2 className="w-5 h-5" />
        </Button>

        {/* Recording indicator + Timer */}
        <div className="flex items-center gap-2 shrink-0">
          <div 
            className={`w-2.5 h-2.5 rounded-full bg-destructive ${isPaused ? '' : 'animate-pulse-record'}`} 
            aria-hidden="true" 
          />
          <span className="text-sm font-mono text-muted-foreground min-w-[40px]" aria-live="off">
            {formatDuration(duration)}
          </span>
        </div>

        {/* Waveform visualization */}
        <div className="flex-1 flex items-center justify-center gap-[3px]" aria-hidden="true">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className={`w-[3px] bg-primary/60 rounded-full ${isPaused ? '' : 'animate-waveform-dot'}`}
              style={{
                animationDelay: isPaused ? '0s' : `${i * 0.05}s`,
                height: isPaused ? '4px' : '8px',
              }}
            />
          ))}
        </div>

        {/* Pause/Resume button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onTogglePause}
          className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label={isPaused ? "Resume recording" : "Pause recording"}
        >
          {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
        </Button>

        {/* Send button */}
        <Button
          onClick={onSend}
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-primary hover:bg-primary/90"
          aria-label="Send voice message"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>

      {/* Live transcript preview */}
      {liveTranscript && (
        <div className="px-3 py-2 bg-muted/50 rounded-lg">
          <p className="text-sm text-foreground leading-relaxed">
            "{liveTranscript}"
          </p>
        </div>
      )}
    </div>
  );
};
